import type { CloudEnvironment } from "../types/CloudEnvironment";
import type { CloudEnvironmentInfo, InputUrlType, NewAccountCloudEnvironmentInfo, } from "../types/CloudEnvironmentInfo";
import { ensureOriginInRequest, ensureOriginInString } from "owa-url/lib/ensureOrigin";
import { getOrigin } from "owa-url/lib/getOrigin";
import { isSovereignEnvironment } from "./isSovereignEnvironment";
import type { ApplicationCloudEnvironment } from "../types/ApplicationCloudEnvironment";

export class CloudEnvironmentInfoImpl implements CloudEnvironmentInfo {
    protected environment: CloudEnvironment;
    protected applicationEnvironment: ApplicationCloudEnvironment | undefined;
    
    constructor(environment: CloudEnvironment, applicationEnvironment?: ApplicationCloudEnvironment) {
        this.environment = environment;
        this.applicationEnvironment = applicationEnvironment;
    }
    
    buildOutlookUrl<T extends string | Request>(originalUrl: T): InputUrlType<T> {
        const outlookWebOrigin = this.getOutlookWebOrigin();
        if (typeof originalUrl === "string") {
            return ensureOriginInString(originalUrl, outlookWebOrigin) as InputUrlType<T>;
        }
        
        return ensureOriginInRequest(originalUrl, outlookWebOrigin) as InputUrlType<T>;
    }
    
    /**
     * Returns the Outlook Web origin to build URLs against. When this environment is the same
     * cloud the application booted from (opaque cloudId equality), prefer the application
     * environment's origin (the origin the native host used to load the app) so there is a single
     * source of truth for the app-cloud origin. Otherwise use the discovered per-environment origin.
     */
    private getOutlookWebOrigin(): string {
        if (this.applicationEnvironment !== undefined &&
            this.environment.cloudId === this.applicationEnvironment.cloudId) {
            return this.applicationEnvironment.outlookWebOrigin;
        }
        
        return this.environment.outlookWebOrigin;
    }
    
    msaAuthenticationEndpoint(): string | undefined {
        return this.environment.msaAuthenticationEndpoint;
    }
    
    orgAuthenticationEndpoint(): string {
        return this.environment.orgAuthenticationEndpoint;
    }
    
    telemetrySafeDiagnosticData(): string {
        return JSON.stringify({
            appOrigin: getOrigin(),
            cloudId: this.environment.cloudId,
            envKey: this.environment.environmentKey,
            msaAuthEndpoint: this.environment.msaAuthenticationEndpoint,
            orgAuthEndpoint: this.environment.orgAuthenticationEndpoint,
            outlookWebOrigin: this.environment.outlookWebOrigin,
            tenantId: this.environment.tenantId,
            usesGlobalDirectory: this.environment.usesGlobalDirectory,
        });
    }
    
    tenantId(): string | undefined {
        return this.environment.tenantId;
    }
}

export class NewAccountCloudEnvironmentInfoImpl extends CloudEnvironmentInfoImpl implements NewAccountCloudEnvironmentInfo {
    isSameAsApplicationCloud(): boolean {
        // Prefer the opaque cloudId comparison, the authoritative "same cloud" signal. Fall back to
        // comparing the resolved Outlook Web origin against the app origin only when there is no
        // application cloud environment to compare against (e.g. the app booted from the
        // default/Public cloud, or older NH version).
        if (this.applicationEnvironment !== undefined) {
            return this.environment.cloudId === this.applicationEnvironment.cloudId;
        }
        
        const applicationOrigin = getOrigin();
        return applicationOrigin === this.buildOutlookUrl(applicationOrigin);
    }
    
    isSovereign(): boolean {
        return isSovereignEnvironment(this.environment);
    }
}
